//* Libraries imports
import { useQuery } from "@tanstack/react-query";

//* Schemas Import
import { 
  processExamFiltersResponse, 
  processExamFiltersParams,
  type ProcessExamFiltersParams 
} from "../../../schemas/common/process/process-exam-filters";

//* Axios Import
import { privateApi } from "../../../services/apis/configs/apiClient";

/**
 * API for process exam filters
 */
const ProcessExamFiltersAPI = {
  /**
   * Fetches process exams with optional filters
   * @param processoId Process ID
   * @param params Filter parameters (search, categoria_id)
   * @returns Filtered exams with categories and questions (according to filters)
   */
  getExamFilters: async function (processoId: number, params?: ProcessExamFiltersParams) {
    try {
      // Validate input parameters
      const validatedParams = processExamFiltersParams.parse(params || {});
      
      const response = await privateApi.get(
        `/student_portal/private/processos/get_processo/${processoId}/provas-filtros`,
        {
          params: validatedParams,
        }
      );

      const validatedResponse = processExamFiltersResponse.parse(response);

      return {
        data: validatedResponse.data,
        success: true
      };
    } catch (error) {
      console.error(`Error fetching exam filters for process ${processoId}:`, error);
      return {
        data: null,
        success: false,
        message: "Could not load exam filters. Please try again later."
      };
    }
  }
};

/**
 * Hook to fetch process exams with filters
 * @param processoId Process ID
 * @param params Filter parameters (search, categoria_id)
 * @returns Query for filtered exams
 */
export function useGetProcessExamFilters(processoId: number, params?: ProcessExamFiltersParams) {
  return useQuery({
    queryKey: ["process-exam-filters", processoId, params],
    queryFn: async () => await ProcessExamFiltersAPI.getExamFilters(processoId, params),
    retry: 2,
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!processoId,
  });
}